import React, { useState, useEffect, useContext } from "react";
import { db } from "services/firebase";
import firebase from "firebase/compat/app";
import { AuthContext } from "context/Auth";
import { isDevAuthFirestoreUser } from "utils/isDevAuthFirestoreUser";
import { ensureDevFacilityLocation } from "config/devAuthCodes";

export const LocationContext = React.createContext();

export const LocationProvider = ({ children }) => {
  const { user, userData } = useContext(AuthContext);

  const [location, setLocation] = useState();
  const [locationLoaded, setLocationLoaded] = useState(false);

  const getLocation = (id) => {
    return db
      .collection("locations")
      .where("users", "array-contains", id)
      .onSnapshot((querySnapshot) => {
        let found = null;
        querySnapshot.forEach((doc) => {
          // Include the Firestore doc id — facility-scoped features (e.g. SavoirCare
          // board) need the location/{facilityId} id, not just the doc fields.
          found = { id: doc.id, ...doc.data() };
          setLocation(found);
        });

        if (!found) {
          setLocation(undefined);
        }

        setLocationLoaded(true);
      });
  };

  useEffect(() => {
    if (user) {
      setLocationLoaded(false);
      return getLocation(user.uid);
    }
    setLocation(undefined);
    setLocationLoaded(false);
    return undefined;
  }, [user]);

  // Dev facility accounts stamp users.facilityId but may predate the membership
  // doc. If the users[] query returned empty, create/link locations/{facilityId}
  // so Status Board / SavoirCare can resolve a facility id.
  useEffect(() => {
    if (!user || !locationLoaded || location || !isDevAuthFirestoreUser(userData)) {
      return undefined;
    }

    const facilityId =
      userData?.facilityId || userData?.clinicId || userData?.location;
    if (!facilityId) return undefined;

    let cancelled = false;
    (async () => {
      try {
        await ensureDevFacilityLocation(db, firebase, {
          uid: user.uid,
          facilityId,
          title: userData?.name || "Dev Facility",
        });
        if (cancelled) return;
        const snap = await db.collection("locations").doc(String(facilityId)).get();
        if (!cancelled && snap.exists) {
          setLocation({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.error("[Location] Failed to ensure dev facility location:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, userData, locationLoaded, location]);

  return (
    <LocationContext.Provider
      value={{
        location,
        locationLoaded,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};
